import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plug, MessageSquare, Terminal, RotateCcw, Power, Activity, Monitor } from "lucide-react";
import { API, agentOutdated, api, fmtDate, fmtIdle, timeAgo } from "@/lib/api";
import { HelpCard } from "@/components/HelpCard";
import { Timeline } from "@/components/machine/Timeline";
import { ChatTab, CommandsTab } from "@/components/machine/LiveTabs";
import { NotesTab } from "@/components/DetailPanel";
import { Health, Inventory, Updates } from "@/pages/RmmMachine";

const Btn = ({ onClick, disabled, primary, id, icon: Icon, children, tone }) => (
  <button onClick={onClick} disabled={disabled} data-testid={id}
    className={`rounded-md text-[13px] h-8 px-3 inline-flex items-center gap-1.5 whitespace-nowrap transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${primary ? "bg-sky-500 text-white font-semibold hover:bg-sky-600" : tone || "border border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
    <Icon className="h-4 w-4" />{children}
  </button>
);

const Thumb = ({ m }) => {
  const [tick, setTick] = useState(Date.now());
  const [ok, setOk] = useState(true);
  useEffect(() => { setOk(true); const t = setInterval(() => setTick(Date.now()), 30000); return () => clearInterval(t); }, [m.machine_id]);
  return (
    <div className="w-52 h-[118px] rounded-lg overflow-hidden bg-slate-800 relative shrink-0 grid place-items-center" data-testid="machine-thumbnail-box">
      {ok && m.online ? <img data-testid="machine-thumbnail" src={`${API}/machines/${m.machine_id}/thumbnail?t=${tick}`} alt="Capture d'écran" onError={() => setOk(false)} className="w-full h-full object-cover" />
        : <span className="text-[11px] text-white/50 text-center px-3 flex flex-col items-center gap-1"><Monitor className="h-5 w-5" />Pas d'aperçu{m.online ? " pour l'instant" : " (hors ligne)"}</span>}
    </div>
  );
};

const Gauge = ({ l, v }) => (
  <div><div className="flex justify-between text-[11px] text-slate-500"><span>{l}</span><span className="font-semibold text-slate-800">{v == null ? "—" : `${Math.round(v)} %`}</span></div>
    <div className="h-1.5 rounded bg-slate-100 mt-1"><div className={`h-full rounded ${v > 80 ? "bg-red-500" : "bg-sky-500"}`} style={{ width: `${v || 0}%` }} /></div></div>
);

// Compact "Infos" tab of the chosen model: Session / Appareil in two columns, gauges on the right.
const Infos = ({ m, group, latest, d }) => {
  const old = m.agent_version && agentOutdated(m.agent_version, latest);
  const rows = [
    ["Session", [["Nom", m.display_name || m.hostname], ["Entreprise", group?.name], ["Site", m.site], ["Département", m.department], ["Type d'appareil", m.device_type || m.os],
      ["Hôtes connectés", m.technicians?.join(", ")], ["Invités connectés", m.online ? `Invité (${fmtDate(m.connected_at)})` : ""],
      ["Utilisateur connecté", m.username ? `${m.hostname}\\${m.username}` : ""], ["Temps d'inactivité", m.online ? fmtIdle(m.idle_s) : ""]]],
    ["Appareil", [["Machine", m.hostname], ["Système d'exploitation", m.os_version], ["Processeur(s)", m.cpu], ["Mémoire", m.ram_gb ? `${m.ram_gb} Go` : ""],
      ["Écran", m.screen_w ? `${m.screen_w} × ${m.screen_h}` : ""], ["IP locale", m.ip_local], ["IP publique", m.ip_public], ["Allumé depuis", m.uptime_s ? fmtIdle(m.uptime_s) : ""],
      ["Version de l'agent", m.agent_version && <span data-testid="info-agent-version" className={old ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold"}>{m.agent_version}{latest && ` / ${latest} (${old ? "à mettre à jour" : "à jour"})`}</span>],
      ["Inscrit le", fmtDate(m.created_at)]]],
  ];
  const h = d?.health || {};
  return (
    <div className="grid grid-cols-[1fr_200px] gap-x-6" data-testid="machine-info-tab">
      <div className="space-y-3">
        <p className="text-[11px] text-slate-400">Dernière mise à jour {timeAgo(m.last_seen)}</p>
        {rows.map(([title, list]) => (
          <div key={title}>
            <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">{title}</p>
            <dl className="text-[12.5px] grid grid-cols-[150px_1fr] gap-y-1">
              {list.map(([k, v]) => <div key={k} className="contents"><dt className="text-slate-500">{k}</dt><dd className="text-slate-900 break-all">{v || <span className="text-slate-300">—</span>}</dd></div>)}
            </dl>
          </div>
        ))}
      </div>
      {d && <div className="space-y-3"><Gauge l="Processeur" v={h.cpu} /><Gauge l="Mémoire" v={h.ram} />{(h.disks || []).map((x) => <Gauge key={x.mount} l={`Disque ${x.mount}`} v={x.percent} />)}</div>}
    </div>
  );
};

const useRmm = (id, enabled) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => (enabled ? api.get(`/rmm/machines/${id}`).then(({ data }) => setD(data)).catch(() => {}) : null), [id, enabled]);
  useEffect(() => { setD(null); load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  return [d, load];
};

const RMM_TABS = ["health", "updates", "software"];

const Empty = ({ stats, welcome }) => (
  <section className="flex-1 grid place-items-center bg-slate-50 p-8" data-testid="detail-empty">
    {welcome ? (
      <div className="w-full max-w-3xl text-center" data-testid="detail-welcome">
        <img src="/illustrations/welcome.png" alt="" className="w-full max-w-sm mx-auto" draggable={false} />
        <p className="text-2xl font-bold text-slate-900 mt-6">Bienvenue sur ScreenView</p>
        <p className="text-sm text-slate-500 mt-1 mb-6">Installez l'agent sur un premier poste : il apparaîtra ici en quelques secondes.</p>
        {welcome}
      </div>
    ) : (
      <div className="w-full max-w-3xl text-center">
        <img src="/illustrations/empty-detail.png" alt="" className="w-full max-w-md mx-auto" draggable={false} />
        {stats && (
          <div className="flex justify-center gap-3 mt-8 text-[13px]" data-testid="detail-empty-stats">
            {[["En ligne", stats.online, "text-emerald-600"], ["Hors ligne", stats.offline, "text-slate-500"], ["À mettre à jour", stats.outdated, "text-red-600"], ["Demandes d'aide", stats.help, "text-orange-600"]].map(([l, n, c]) => (
              <div key={l} className="rounded-lg bg-white border border-slate-200 px-4 py-2 min-w-[96px]"><p className={`text-xl font-bold ${c}`}>{n}</p><p className="text-slate-500 text-[11px]">{l}</p></div>
            ))}
          </div>
        )}
      </div>
    )}
  </section>
);

export const DetailCard2 = ({ m, latest, group, help, onHelpResolved, onConnect, onSaved, canConnect, act, tab, setTab, rmm, stats, welcome }) => {
  const [d, reload] = useRmm(m?.machine_id, !!m && rmm);
  if (!m) return <Empty stats={stats} welcome={welcome} />;
  const alerts = (d?.alerts || []).filter((a) => a.status === "open").length;
  const upd = d ? (d.os_updates || 0) + (d.app_updates || 0) : 0;
  const tabs = [["info", "Infos"], ...(rmm ? [["health", "Santé", alerts], ["updates", "Mises à jour", upd], ["software", "Logiciels"]] : []),
    ["history", "Connexions"], ["chat", "Messages"], ["commands", "Commandes"], ["notes", "Notes"]];
  const cur = !rmm && RMM_TABS.includes(tab) ? "info" : tab;
  return (
    <section className="flex-1 min-w-0 bg-slate-50 p-4 overflow-hidden flex flex-col" data-testid="machine-detail-panel">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col min-h-0 flex-1">
        <div className="flex gap-4 p-4">
          <Thumb m={m} />
          <div className="min-w-0 flex-1">
            <p className="text-lg font-bold text-slate-900 flex items-center gap-2 min-w-0">
              <span className={`h-2 w-2 rounded-full shrink-0 ${m.online ? "bg-emerald-500" : "bg-slate-300"}`} />
              <span className="truncate" data-testid="detail-machine-name">{m.display_name || m.hostname}</span>
            </p>
            <p className="text-sm text-slate-500 truncate">{[group?.name || "Non classé", m.username, m.os_version || m.os].filter(Boolean).join(" · ")}{!m.online && " · hors ligne"}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Btn primary icon={Plug} disabled={!m.online || !canConnect} onClick={() => onConnect(m)} id="detail-connect-btn">Se connecter</Btn>
              <Btn icon={MessageSquare} onClick={() => setTab("chat")} id="detail-message-btn">Message</Btn>
              <Btn icon={Terminal} onClick={() => setTab("commands")} id="detail-command-btn">Commande</Btn>
              {m.online
                ? <Btn icon={RotateCcw} onClick={() => act.restart(m)} id="detail-restart-btn">Redémarrer</Btn>
                : <Btn icon={Power} onClick={() => act.wake(m)} id="detail-wake-btn" tone="border border-emerald-300 text-emerald-700 hover:bg-emerald-50">Réveiller (Wake-on-LAN)</Btn>}
              {rmm && <Link to={`/app/rmm/${m.machine_id}`} data-testid="detail-rmm-btn" className="rounded-md text-[13px] h-8 px-3 inline-flex items-center gap-1.5 text-slate-500 hover:text-sky-700"><Activity className="h-4 w-4" />Fiche RMM</Link>}
            </div>
          </div>
        </div>
        {help && <HelpCard reqs={help} m={m} onConnect={onConnect} canConnect={canConnect} onResolved={onHelpResolved} />}
        <div className="flex gap-4 px-4 mt-2 border-y border-slate-200 text-[13px] overflow-x-auto shrink-0">
          {tabs.map(([id, label, n]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`detail-tab-${id}`}
              className={`py-2.5 border-b-2 whitespace-nowrap flex items-center gap-1 transition-colors ${cur === id ? "border-sky-600 text-sky-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {label}{n > 0 && <span className="text-[10px] bg-red-500 text-white rounded-full px-1.5">{n}</span>}
            </button>
          ))}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {cur === "info" && <Infos m={m} group={group} latest={latest} d={d} />}
          {RMM_TABS.includes(cur) && !d && <p className="text-sm text-slate-500">Chargement…</p>}
          {cur === "health" && d && <>
            {(d.alerts || []).filter((a) => a.status === "open").map((a) => (
              <p key={a.alert_id} className="mb-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[13px]" data-testid={`sv2-alert-${a.alert_id}`}><b>{a.label}</b> <span className="text-slate-600">— {a.message}</span></p>
            ))}
            <Health d={d} />
          </>}
          {cur === "updates" && d && <Updates d={d} id={m.machine_id} reload={reload} />}
          {cur === "software" && d && <Inventory d={d} id={m.machine_id} />}
          {cur === "history" && <Timeline id={m.machine_id} />}
          {cur === "chat" && <ChatTab m={m} />}
          {cur === "commands" && <CommandsTab m={m} />}
          {cur === "notes" && <NotesTab m={m} onSaved={onSaved} />}
        </div>
      </div>
    </section>
  );
};
